'use client';

import Image from 'next/image';
import { type JSX, useRef, useState } from 'react';

import { ChevronDown } from '@videoblocks/react-icons';

import SingleColumnMenu from '../desktop/SingleColumnMenu';
import { telemetryClickHandler } from '../telemetryHandler';
import type { ActionItemConfig } from '../types';

interface IconActionProps {
    item: ActionItemConfig & { type: 'icon' };
    darkHeader?: boolean;
}

export default function IconAction({ item, darkHeader = false }: IconActionProps): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const chevronIcon = item.chevron ? (
        <ChevronDown className={`h-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
    ) : null;

    if (!item.dropdown) {
        return (
            <a
                href={item.href}
                aria-label={item.ariaLabel}
                className="flex items-center gap-1 hover:opacity-80"
                onClick={telemetryClickHandler(item.telemetrySlug, item.telemetryEvents)}
            >
                <Image src={item.icon} alt="" width={24} height={24} className={darkHeader ? 'invert' : ''} />
                {item.label && (
                    <span className={`ml-2 text-base font-semibold ${darkHeader ? 'text-white' : 'text-gray-900'}`}>
                        {item.label}
                    </span>
                )}
                {chevronIcon}
            </a>
        );
    }

    return (
        <div
            ref={containerRef}
            className="relative"
            onMouseEnter={(): void => setIsOpen(true)}
            onMouseLeave={(): void => setIsOpen(false)}
            onKeyDown={(e: React.KeyboardEvent): void => {
                if (e.key === 'Escape') {
                    setIsOpen(false);
                }
            }}
            onBlur={(e: React.FocusEvent): void => {
                if (!containerRef.current?.contains(e.relatedTarget as Node)) {
                    setIsOpen(false);
                }
            }}
        >
            <button
                type="button"
                className="flex items-center gap-2 hover:opacity-80"
                aria-label={item.ariaLabel}
                aria-expanded={isOpen}
                aria-haspopup="true"
                onClick={(): void => setIsOpen((prev) => !prev)}
            >
                <Image src={item.icon} alt="" width={24} height={24} className={darkHeader ? 'invert' : ''} />
                {item.label && (
                    <span className={`ml-2 text-sm font-normal ${darkHeader ? 'text-white' : 'text-gray-900'}`}>
                        {item.label}
                    </span>
                )}
                {chevronIcon}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full z-50 mt-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md whitespace-nowrap">
                    <SingleColumnMenu links={item.dropdown.links} />
                </div>
            )}
        </div>
    );
}
